import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchBrand, setCallInProgress, setBrandSwitcherOpen } from '../features/channelSlice';

// Helper function to get brand logo.
// The canonical key is top-level `logoUrl` (camelCase), promoted by channelSlice on load/switch.
const getBrandLogo = (brand) => {
  // 1. Top-level logoUrl — canonical, always set by channelSlice
  if (brand?.logoUrl) return brand.logoUrl;

  // 2. Parse brand_json for brands not yet through channelSlice
  let brandJson = brand?.brand_json;
  if (typeof brandJson === 'string') {
    try { brandJson = JSON.parse(brandJson); } catch (e) { brandJson = null; }
  }
  if (brandJson?.logoUrl) return brandJson.logoUrl;

  return null;
};

// Resolve the primary colour for a brand (ring, active state, etc.)
// Prefers direct brandColour field from /brands API, falls back to parsing nested structures.
const getBrandColor = (brand) => {
  if (!brand) return '#44C2FD';
  
  // 1. Primary: Use brandColour from /brands API response
  if (brand.brandColour) return brand.brandColour;
  
  // 2. Fallback to existing color fields
  if (brand.colors?.gradientColor1) return brand.colors.gradientColor1;
  if (brand.colors?.gradient1) return brand.colors.gradient1;
  if (brand.channelBranding?.agent?.color) return brand.channelBranding.agent.color;
  if (brand.channelBranding?.agent?.gradientColor1) return brand.channelBranding.agent.gradientColor1;
  // brand_json fallback (populated after a full brand fetch/apply)
  let brandJson = brand.brand_json;
  if (typeof brandJson === 'string') { try { brandJson = JSON.parse(brandJson); } catch (e) { brandJson = null; } }
  if (brandJson?.channelBranding?.agent?.color) return brandJson.channelBranding.agent.color;
  if (brandJson?.channelBranding?.agent?.gradientColor1) return brandJson.channelBranding.agent.gradientColor1;
  if (brandJson?.colors?.gradientColor1) return brandJson.colors.gradientColor1;
  return '#44C2FD';
};

function BrandSwitcher({ isOpen = false, onClose = () => {}, mobile = false }) {
  const dispatch = useDispatch();
  const { 
    currentBrand, 
    availableBrands, 
    brandStatus,
    isHelpModalOpen
  } = useSelector(state => state.channel);
  
  const { isLiveModalOpen } = useSelector(state => state.live);
  
  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = useRef(null);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  // Mirror open-state into Redux so GuidedTour can gate nudges off while open.
  // Reported from the component so both the desktop (Actions) and mobile (App)
  // mounts are covered from one place.
  useEffect(() => {
    dispatch(setBrandSwitcherOpen(isOpen));
    return () => { dispatch(setBrandSwitcherOpen(false)); };
  }, [isOpen, dispatch]);

  // Close BrandSwitcher when clicking outside (desktop mode only)
  useEffect(() => {
    if (!isOpen || mobile) return;

    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    // Add a small delay to prevent immediate closure when opening
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, mobile, onClose]);

  // Close BrandSwitcher when another modal opens
  useEffect(() => {
    if (isOpen && (isHelpModalOpen || isLiveModalOpen)) {
      onClose();
    }
  }, [isHelpModalOpen, isLiveModalOpen, isOpen, onClose]);

  const filteredBrands = (availableBrands || []).filter(brand =>
    brand.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectBrand = async (brandId) => {
    console.log('🔄 BrandSwitcher: handleSelectBrand called with brandId:', brandId, 'currentBrand?.id:', currentBrand?.id);
    
    if (brandId === currentBrand?.id) {
      console.log('⏭️ BrandSwitcher: Same brand selected, closing without action');
      onClose();
      return;
    }

    try {
      // End any active call before switching brand (context will change)
      dispatch(setCallInProgress(false));
      
      console.log('📡 BrandSwitcher: Dispatching fetchBrand with brandId:', brandId);
      const result = await dispatch(fetchBrand(brandId)).unwrap();
      console.log('✅ BrandSwitcher: fetchBrand completed successfully, result:', result);
      
      // Verify localStorage was updated
      const verifyBrandId = localStorage.getItem('selectedBrandId');
      console.log('🔍 BrandSwitcher: After fetchBrand, localStorage.getItem("selectedBrandId"):', verifyBrandId);
      
      onClose();
    } catch (error) {
      console.error('❌ BrandSwitcher: Failed to load brand:', error);
    }
  };

  if (!isOpen) return null;

  // Mobile: Full-screen centered modal
  // Desktop: Popout next to the actions button
  const isMobileModal = mobile;

  return (
    <>
      {/* Modal backdrop - only for mobile */}
      {isMobileModal && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-out'
          }}
        />
      )}
      
      {/* Modal content */}
      <div
        ref={modalRef}
        style={{
          position: isMobileModal ? 'fixed' : 'absolute',
          top: isMobileModal ? '50%' : '0',
          left: isMobileModal ? '50%' : 'calc(100% + 8px)',
          right: isMobileModal ? 'auto' : 'auto',
          transform: isMobileModal ? 'translate(-50%, -50%)' : 'none',
          width: isMobileModal ? 'calc(100vw - 32px)' : '340px',
          maxWidth: isMobileModal ? '420px' : '340px',
          maxHeight: isMobileModal ? '70vh' : '500px',
          background: 'var(--cara-surface)',
          border: '2px solid rgba(68, 194, 253, 0.2)',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7), 0 0 40px rgba(68, 194, 253, 0.1)',
          overflow: 'hidden',
          zIndex: isMobileModal ? 10001 : 1000,
          animation: isMobileModal ? 'slideInMobile 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'slideIn 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          fontFamily: 'Lato, -apple-system, BlinkMacSystemFont, sans-serif'
        }}
      >
        <style>
          {`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes slideIn {
              from {
                opacity: 0;
                transform: translateX(10px);
              }
              to {
                opacity: 1;
                transform: translateX(0);
              }
            }
            @keyframes slideInMobile {
              from {
                opacity: 0;
                transform: translate(-50%, -50%) scale(0.92);
              }
              to {
                opacity: 1;
                transform: translate(-50%, -50%) scale(1);
              }
            }
            @keyframes shine {
              0%, 100% { transform: translateX(-100%); }
              50% { transform: translateX(100%); }
            }
            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }
            .brand-list-scroll::-webkit-scrollbar {
              width: 8px;
            }
            .brand-list-scroll::-webkit-scrollbar-track {
              background: #111827;
            }
            .brand-list-scroll::-webkit-scrollbar-thumb {
              background: #374151;
              border-radius: 4px;
            }
            .brand-list-scroll::-webkit-scrollbar-thumb:hover {
              background: #4b5563;
            }
          `}
        </style>
        
        {/* Modal header with title and close button - only for mobile */}
        {isMobileModal && (
          <div 
            style={{
              padding: '20px',
              borderBottom: '1px solid rgba(107, 114, 128, 0.3)',
              background: 'var(--cara-surface-2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {/* Subtle animated shine effect */}
            <div 
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.05), transparent)',
                animation: 'shine 3s ease-in-out infinite',
                transform: 'translateX(-100%)',
              }}
            />
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              position: 'relative',
              zIndex: 10
            }}>            <div>
              <h3 style={{
                margin: 0,
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--cara-text)',
                letterSpacing: '0.02em'
              }}>
                Brand
              </h3>
              <p style={{
                margin: 0,
                fontSize: '12px',
                color: 'var(--cara-text-muted)'
              }}>
                Choose your brand identity
              </p>
            </div>
            </div>
            
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--cara-text-muted)',
                cursor: 'pointer',
                padding: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                transition: 'all 0.2s',
                position: 'relative',
                zIndex: 10
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(68, 194, 253, 0.1)';
                e.currentTarget.style.color = 'var(--cara-text)';
                e.currentTarget.style.transform = 'rotate(90deg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = 'var(--cara-text-muted)';
                e.currentTarget.style.transform = 'rotate(0deg)';
              }}
            >
              <span className="material-icons" style={{ fontSize: '24px' }}>close</span>
            </button>
          </div>
        )}
        
        {/* Search input */}
        <div 
          style={{
            padding: '16px',
            borderBottom: '1px solid rgba(107, 114, 128, 0.2)',
            background: 'var(--cara-surface-2)'
          }}
        >
          <div style={{ position: 'relative' }}>
            <span className="material-icons" style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--cara-text-muted)',
              fontSize: '20px',
              pointerEvents: 'none'
            }}>
              search
            </span>
            <input
              type="text"
              placeholder="Search brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 44px',
                border: '1px solid rgba(68, 194, 253, 0.25)',
                borderRadius: '10px',
                fontSize: '14px',
                fontFamily: 'inherit',
                outline: 'none',
                background: 'var(--cara-input-bg)',
                color: 'var(--cara-text)',
                transition: 'all 0.3s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = 'rgba(68, 194, 253, 0.5)';
                e.target.style.boxShadow = '0 0 0 2px rgba(68, 194, 253, 0.15)';
                e.target.style.background = '#1a2332';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(68, 194, 253, 0.25)';
                e.target.style.boxShadow = 'none';
                e.target.style.background = 'var(--cara-surface)';
              }}
            />
          </div>
        </div>

        {/* Brand list */}
        <div className="brand-list-scroll" style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'hidden' }}>
          {filteredBrands.length > 0 ? (
            filteredBrands.map((brand, index) => {
              const isActive = currentBrand?.id === brand.id;
              const brandColor = brand.brandColour || getBrandColor(brand);
              return (
                <button
                  key={brand.id}
                  onClick={() => handleSelectBrand(brand.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    width: '100%',
                    padding: '14px 16px',
                    border: 'none',
                    borderBottom: '1px solid rgba(107, 114, 128, 0.15)',
                    background: isActive 
                      ? `linear-gradient(to right, ${brandColor}26, ${brandColor}1a)` 
                      : 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    position: 'relative',
                    overflow: 'hidden',
                    color: 'var(--cara-text)',
                    fontFamily: 'inherit',
                    fontSize: '14px',
                    fontWeight: isActive ? '600' : 'normal'
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'rgba(68, 194, 253, 0.08)';
                      e.currentTarget.style.transform = 'translateX(4px)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.transform = 'translateX(0)';
                    }
                  }}
                >
                  {/* Shimmer effect on hover for inactive items */}
                  {!isActive && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.1), transparent)',
                      transform: 'translateX(-100%)',
                      transition: 'transform 0.7s',
                      pointerEvents: 'none'
                    }} className="shimmer-effect" />
                  )}
                  
                  {/* Brand logo in circle - always show, with fallback icon */}
                  {(() => {
                    // Get logo URL using the comprehensive getBrandLogo helper
                    const logoUrl = getBrandLogo(brand);
                    return (
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: brandColor,
                        border: isActive ? `1px solid #44C2FD` : '1px solid rgba(255, 255, 255, 0.4)',
                        flexShrink: 0,
                        transition: 'all 0.3s'
                      }}>
                        {logoUrl ? (
                          <img 
                            src={logoUrl} 
                            alt={brand.name}
                            style={{
                              width: '32px',
                              height: '32px',
                              objectFit: 'contain'
                            }}
                            onError={(e) => {
                              // Hide broken image and show fallback
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <span 
                            className="material-icons" 
                            style={{ 
                              fontSize: '22px', 
                              color: 'white'
                            }}
                          >
                            business
                          </span>
                        )}
                      </div>
                    );
                  })()}
                  <span>{brand.name}</span>
                  {isActive && (
                    <span 
                      className="material-icons" 
                      style={{ 
                        fontSize: '20px', 
                        color: '#44C2FD',
                        marginLeft: 'auto'
                      }}
                    >
                      check
                    </span>
                  )}
                </button>
              );
            })
          ) : (
            <div 
              style={{
                padding: '24px 16px',
                textAlign: 'center',
                color: '#6b7280',
                fontSize: '14px'
              }}
            >
              {searchQuery ? 'No brands found' : 'No brands available'}
            </div>
          )}
        </div>

        {/* Loading indicator */}
        {brandStatus === 'loading' && (
          <div 
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              background: 'var(--cara-surface-2)',
              borderTop: '1px solid rgba(107, 114, 128, 0.5)',
              color: 'var(--cara-text-muted)',
              fontSize: '14px'
            }}
          >
            <div 
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid rgba(107, 114, 128, 0.5)',
                borderTopColor: '#44C2FD',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite'
              }}
            />
            Loading...
          </div>
        )}
      </div>
    </>
  );
}

export default BrandSwitcher;
